import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import type { LanguageDto, UserCreateRequest, UserDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { UploadFileButton } from "@/components/Button/UploadFileButton";
import { DataTable } from "@/components/DataTable/DataTable";
import { type Column, type Data, parseData } from "@/components/DataTable/parser";
import { downloadExcel, parseExcel } from "@/components/DataTable/sheet";
import { validate } from "@/components/DataTable/validation";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormSearchableSelect } from "@/components/Form/FormSearchableSelect";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { User } from "@/components/User/User";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { formatAddress, joinSplitAddressColumns } from "@/helpers/address";
import { isMaybeInvalidEmail, isValidEmail } from "@/helpers/email-validation";
import { parseFileContent } from "@/helpers/file-parse";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useOnDropFiles } from "@/hooks/useOnDropFiles";
import { useOnPaste } from "@/hooks/useOnPaste";
import { usePermission } from "@/hooks/usePermission";
import { usePrompt } from "@/hooks/usePrompt";
import { useSlug } from "@/hooks/useSlug";
import { canAssignRoles } from "@/modules/users/permissions";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { useCompanyQueries } from "@/queries/companies/queryOptions";
import { fileMutations } from "@/queries/files/mutations";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { useProjectQueries } from "@/queries/project/queryOptions";
import { userMutations } from "@/queries/users/mutations";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { UserBulkUploadConfirmModal } from "./components/UserBulkUploadConfirmModal";
import { USER_BULK_UPLOAD_FORM_ID } from "./constants";

type FormValues = {
  signOffUser?: UserDto;
};

export function UserBulkUploadPage(): React.ReactNode {
  const [isUploading, setIsUploading] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isConfirmModalOpened, confirmModalHandler] = useBool(false);
  const [data, setData] = useState<Data<NonNullable<typeof columns>>>();
  const [uploadedFile, setUploadedFile] = useState<File>();

  const slug = useSlug();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const hasPermission = usePermission();
  const canAssignUserRoles = hasPermission(canAssignRoles);
  const canManageRems = hasPermission((x) => x.integrations.canManageRems);
  const generalQueries = useGeneralQueries();
  const integrationQueries = useIntegrationQueries();
  const {
    data: uiLanguages,
    isFetching: isFetchingUiLanguages,
    error: uiLanguagesError,
  } = useQuery(generalQueries.languages({ subset: "ui" }));
  const {
    data: remsConfiguration,
    isPending: isPendingRemsConfiguration,
    error: remsConfigurationError,
  } = useQuery({
    ...integrationQueries.remsConfiguration(),
    enabled: canManageRems,
  });
  const canUploadRemsMappings = canManageRems && remsConfiguration?.contracts.enabled === true;
  const formMethods = useForm<FormValues>({
    mode: "onChange",
  });
  const hasUnsavedFormChanges =
    Object.keys(formMethods.formState.dirtyFields).length > 0 && !formMethods.formState.isSubmitted;

  usePrompt(
    isUploading ? t("common.label.wait-and-not-exit-page") : t("component.prompt.confirmation"),
    isUploading || hasUnsavedFormChanges,
  );

  const showFlashToast = useFlashToast();
  const projectQueries = useProjectQueries();
  const {
    data: project,
    isPending: isPendingProject,
    error: projectError,
    refetch: refetchProject,
  } = useQuery(projectQueries.details());
  const userQueries = useUserQueries();
  const { data: admins, isPending: isPendingAdmins, error: adminsError } = useQuery(userQueries.listAdmins());
  const addressQueries = useAddressQueries();
  const {
    data: addresses,
    isLoading: isLoadingAddresses,
    error: errorAddresses,
  } = useQuery({
    ...addressQueries.list(),
    enabled: project?.type === "addressBased",
  });
  const companyQueries = useCompanyQueries();
  const {
    data: companies,
    isLoading: isLoadingCompanies,
    error: errorCompanies,
  } = useQuery({
    ...companyQueries.list(),
    enabled: project?.type === "companyBased",
  });
  const permissionQueries = usePermissionQueries();
  const {
    data: roles,
    isLoading: isLoadingRoles,
    error: rolesError,
  } = useQuery({
    ...permissionQueries.listRoles(),
    enabled: canAssignUserRoles,
  });
  const {
    data: users,
    isPending: isPendingUsers,
    error: errorUsers,
  } = useQuery({
    ...userQueries.list({
      params: {
        Offset: 0,
        Limit: 10000,
        Statuses: ["invited", "onboarded"],
        RoleIds: canAssignUserRoles ? roles?.map((x) => x.id) : undefined,
      },
    }),
    enabled: !canAssignUserRoles || isDefined(roles),
  });
  const uploadUserMutation = userMutations.useUploadUser();
  const { mutateAsync: uploadDocumentAsync } = fileMutations.useAddDocument();

  const columns = useMemo(() => {
    if (
      !project ||
      (project.type === "addressBased" && !isDefined(addresses)) ||
      (project.type === "companyBased" && !isDefined(companies)) ||
      !isDefined(users) ||
      !isDefined(uiLanguages) ||
      (canAssignUserRoles && !isDefined(roles))
    ) {
      return undefined;
    }

    const languageMap = new Map(uiLanguages.map((x) => [x.id, x.description]));
    const roleNames = roles?.map((role) => role.name) ?? [];
    const defaultRoleName = roles?.find((role) => role.type === "resident")?.name;

    return [
      project.type === "addressBased"
        ? {
            name: "Address",
            alias: ["adres", "streetname", "straatnaam", "straat", "street"],
            rules: {
              required: (row) => {
                if (canAssignUserRoles) {
                  const roleName = (row as Record<string, string | undefined>)["Role"];
                  const role = roles?.find((r) => r.name === roleName);
                  if (role && !role.isResidentType) {
                    return undefined;
                  }
                }

                return t("page.user-bulk-upload.validation.address.required");
              },
              oneOf: {
                error: t("page.user-bulk-upload.validation.address.unknown"),
                values: () => addresses!.items.map(formatAddress),
              },
            },
            warnings: {
              unique: t("page.user-bulk-upload.validation.address.unique"),
            },
          }
        : {
            name: "Company",
            alias: ["bedrijf", "organisatie", "business", "corporation"],
            rules: {
              required: (row) => {
                if (canAssignUserRoles) {
                  const roleName = (row as Record<string, string | undefined>)["Role"];
                  const role = roles?.find((r) => r.name === roleName);
                  if (role && !role.isResidentType) {
                    return undefined;
                  }
                }

                return t("page.user-bulk-upload.validation.company.required");
              },
              oneOf: {
                error: t("page.user-bulk-upload.validation.company.unknown"),
                values: () => companies!.items.map((x) => x.name),
              },
            },
          },
      {
        name: "First name",
        alias: ["voornaam", "voorletters"],
        rules: { required: t("page.user-bulk-upload.validation.first-name.required") },
      },
      {
        name: "Last name",
        alias: ["surname", "achternaam"],
        rules: { required: t("page.user-bulk-upload.validation.last-name.required") },
      },
      {
        name: "Email",
        alias: ["emailaddress", "mailadres"],
        rules: {
          required: t("page.user-bulk-upload.validation.email.required"),
          validate: {
            error: t("page.user-bulk-upload.validation.email.invalid"),
            fn: (input) => typeof input === "string" && isValidEmail(input.trim()),
          },
          notOneOf: {
            error: t("page.user-bulk-upload.validation.email.existing"),
            values: users.filter((x) => isDefined(x.email)).map((x) => x.email!),
            ignoreCase: true,
          },
          unique: { error: t("page.user-bulk-upload.validation.email.repeated"), ignoreCase: true },
        },
        warnings: {
          validate: {
            error: t("page.user-bulk-upload.validation.email.maybe-invalid"),
            fn: (input) => typeof input === "string" && !isMaybeInvalidEmail(input.trim()),
          },
        },
      },
      {
        name: "Mobile",
        alias: ["phone number", "telefoonnummer"],
        rules: {
          match: {
            error: t("page.user-bulk-upload.validation.mobile.invalid"),
            regex: /^\+?\d+$/,
          },
        },
        transform: (s) => {
          if (typeof s === "number") {
            s = s.toString();
          }

          return s.replaceAll(" ", "").replaceAll("-", "").replaceAll("(", "").replaceAll(")", "");
        },
      },
      {
        name: "Language",
        alias: ["taal"],
        defaultValue: uiLanguages[0].id,
        rules: {
          oneOf: {
            error: t("page.user-bulk-upload.validation.language.invalid"),
            values: () => uiLanguages.map((x) => x.id),
          },
        },
        valueAlias: {
          dutch: "nl",
          nederlands: "nl",
          german: "de",
          duits: "de",
          deutsch: "de",
          engels: "en",
          english: "en",
        },
        render(s) {
          return languageMap.get(s as LanguageDto["id"]) || s;
        },
        transform: (s) => {
          if (typeof s === "string") {
            return s.toLowerCase();
          }

          return s;
        },
      },
      ...(canAssignUserRoles
        ? [
            {
              name: "Role",
              alias: ["rol"],
              defaultValue: defaultRoleName,
              rules: {
                oneOf: {
                  error: t("page.user-bulk-upload.validation.role.invalid"),
                  values: () => roleNames,
                },
              },
            },
          ]
        : []),
      ...(canUploadRemsMappings
        ? [
            {
              name: "REMS contact ID",
              alias: ["rems contact", "contact id"],
            },
            {
              name: "REMS contract ID",
              alias: ["rems contract", "relation id", "contract id"],
            },
          ]
        : []),
    ] as const satisfies readonly Column[];
  }, [project, addresses, companies, users, uiLanguages, canAssignUserRoles, canUploadRemsMappings, roles, t]);

  async function handleUpload(formData: FormValues) {
    if (!data) return;

    try {
      setIsUploading(true);
      setData((oldData) =>
        oldData?.map((row) =>
          row.uploadStatus === "SUCCESS" ? row : ({ ...row, uploadStatus: "QUEUED" } as typeof row),
        ),
      );

      // Upload the file to the backend first (for logging/audit purposes)
      if (uploadedFile) {
        await uploadDocumentAsync({ file: uploadedFile });
      }

      let successCount = 0;
      for (let idx = 0; idx < data.length; idx++) {
        const row = data[idx];
        if (row.uploadStatus === "SUCCESS") {
          successCount++;
          continue;
        }

        updateRow(idx, "uploadStatus", "UPLOADING");
        const rowData = row as Record<string, string>;
        try {
          const payload: UserCreateRequest = {
            email: rowData.Email.trim(),
            firstName: rowData["First name"],
            lastName: rowData["Last name"],
            uiLanguage: rowData["Language"] as any,
            addressId: mapAddressValue(rowData.Address),
            companyId: mapCompanyValue(rowData.Company),
            mobileNumber: rowData["Mobile"],
            emailSignOffUserId: formData.signOffUser?.id,
          };

          const roleId = mapRoleValue(rowData.Role);
          if (roleId) {
            payload.roleId = roleId;
          }

          const role = roleId ? roles?.find((item) => item.id === roleId) : undefined;
          const isResident = role ? role.isResidentType : !canAssignUserRoles;
          if (canUploadRemsMappings && isResident) {
            payload.remsContactId = rowData["REMS contact ID"]?.trim() || undefined;
            payload.remsContractId = rowData["REMS contract ID"]?.trim() || undefined;
          }

          await uploadUserMutation.mutateAsync(payload);
          updateRow(idx, "uploadStatus", "SUCCESS");
          successCount++;
        } catch {
          updateRow(idx, "uploadStatus", "ERROR");
        }
      }

      showFlashToast({
        message: t("page.user-bulk-upload.notification.success", { count: successCount }),
        variant: "success",
      });

      // If project was inactive and has users uploaded, refetch project to update its query cache
      if (project?.activeState === "unknown" && data.length > 0) {
        await refetchProject();
      }
    } finally {
      setIsUploading(false);
      setData((oldData) =>
        oldData?.map((row) => (row.uploadStatus === "QUEUED" ? ({ ...row, uploadStatus: "NONE" } as typeof row) : row)),
      );
    }

    confirmModalHandler.setFalse();
  }

  function mapAddressValue(addressName: string) {
    if (project?.type !== "addressBased") {
      return;
    }

    if (!addresses) {
      throw new Error("No addresses available yet");
    }

    for (const address of addresses.items) {
      if (addressName === formatAddress(address)) {
        return address.id;
      }
    }
  }

  function mapCompanyValue(companyName: string) {
    if (project?.type !== "companyBased") {
      return;
    }

    if (!companies) {
      throw new Error("No companies available yet");
    }

    for (const company of companies.items) {
      if (companyName === company.name) {
        return company.id;
      }
    }
  }

  function mapRoleValue(roleName: string | undefined) {
    if (!canAssignUserRoles || !roleName) {
      return;
    }

    return roles?.find((role) => role.name === roleName)?.id;
  }

  function updateRow(row: number, column: string, value: string) {
    setData((oldData) => {
      if (!oldData) {
        throw new Error("No existing data");
      }

      const newData = [...oldData];
      const rowNumber = Number(row);
      const oldRow = oldData[rowNumber];
      const newRow = { ...oldRow, [column]: value };
      newData.splice(rowNumber, 1, newRow);

      return newData;
    });
  }

  function removeRow(row: number) {
    setData((oldData) => {
      if (!oldData) {
        return;
      }

      const newData = [...oldData];
      newData.splice(row, 1);

      // No more data remaining, reset form
      if (newData.length === 0) {
        return undefined;
      }

      return newData;
    });
  }

  const loadCsvFile = useCallback(
    async (...args: Parameters<typeof parseExcel>) => {
      try {
        setIsParsingFile(true);
        const rows = await parseExcel(...args);
        const normalizedRows =
          project?.type === "addressBased" ? joinSplitAddressColumns(rows as object[]) : (rows as object[]);
        const parsedData = parseData(normalizedRows, columns!);
        const preprocessedData = preprocessData(parsedData);
        setData(preprocessedData);
      } finally {
        setIsParsingFile(false);
      }
    },
    [columns, project?.type],
  );

  useOnDropFiles(
    useCallback(
      (files) => {
        const file = files?.[0]?.getAsFile();
        if (file) {
          void file
            .text()
            .then(loadCsvFile)
            .catch((e) => alert(`Could not drop data: ${e}`));
        }
      },
      [loadCsvFile],
    ),
    !data && !isParsingFile,
  );
  useOnDropFiles(
    useCallback(
      async (files) => {
        const file = files?.[0].getAsFile();
        if (file) {
          try {
            const content = await parseFileContent(file);
            await loadCsvFile(content);
          } catch (err) {
            alert(err);
          }
        }
      },
      [loadCsvFile],
    ),
    !data && !isParsingFile,
  );
  useOnPaste(
    useCallback(
      (file) => {
        void loadCsvFile(file).catch((e) => alert(`Could not paste data: ${e}`));
      },
      [loadCsvFile],
    ),
    !data && !isParsingFile,
  );

  function handleDownloadExampleFile() {
    const columnNames = columns?.map((x) => x.name) || [];
    const locationLines =
      (project?.type === "addressBased"
        ? addresses?.items.filter((x) => x.users.length === 0).map(formatAddress)
        : companies?.items.filter((x) => x.activeUserCount + (x.inactiveUserCount || 0) === 0).map((x) => x.name)) ||
      [];
    const fileName = `user-upload-${sessionUser.project.name}`;
    const roleIndex = columnNames.indexOf("Role");
    const defaultRoleName = roles?.find((role) => role.type === "resident")?.name;
    const lines = [
      columnNames,
      ...locationLines.map((line) => {
        const row = Array.from<string>({ length: columnNames.length }).fill("");
        row[0] = line;
        if (roleIndex !== -1 && defaultRoleName) row[roleIndex] = defaultRoleName;

        return row;
      }),
    ];

    const optionsSheets: { sheetName: string; values: string[] }[] = [];

    if (canAssignUserRoles && roles) {
      optionsSheets.push({ sheetName: "Role options", values: roles.map((role) => role.name) });
    }

    return downloadExcel(fileName, lines, { optionsSheets });
  }

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    const file = target.files?.[0];

    if (file) {
      try {
        setUploadedFile(file);
        const contents = await parseFileContent(file);
        await loadCsvFile(contents);
      } catch (e) {
        alert(e);
        target.value = "";
      }
    }
  };

  const handleClickUpload = async () => {
    const isFormValid = await formMethods.trigger();

    if (isFormValid) {
      confirmModalHandler.setTrue();
    }
  };

  const dataState = useMemo(() => (data && columns ? validate(columns, data) : undefined), [data, columns]);

  const allRowsAreNonResidents = useMemo(() => {
    if (!canAssignUserRoles || !roles || !data || data.length === 0) return false;

    return data.every((row) => {
      const roleName = (row as Record<string, string | undefined>)["Role"];
      const role = roles.find((r) => r.name === roleName);

      return role !== undefined && !role.isResidentType;
    });
  }, [canAssignUserRoles, roles, data]);

  if (
    isPendingProject ||
    isLoadingAddresses ||
    isPendingUsers ||
    isFetchingUiLanguages ||
    (canManageRems && isPendingRemsConfiguration) ||
    isPendingAdmins ||
    isLoadingCompanies ||
    isLoadingRoles
  ) {
    return <FullSizeLoader withPadding />;
  }

  const error =
    projectError ||
    errorAddresses ||
    errorUsers ||
    uiLanguagesError ||
    adminsError ||
    errorCompanies ||
    rolesError ||
    remsConfigurationError;
  if (!columns || error) {
    return <ErrorPage error={error ?? t("page.user-bulk-upload.error.no-columns")} />;
  }

  const isConfirmationRequired = project.activeState === "unknown" && !allRowsAreNonResidents;
  const isSubmitDisabled = !dataState?.valid || data?.length === 0 || data?.every((x) => x.uploadStatus === "SUCCESS");
  const isBulkUploadSuccessful = data?.every((x) => x.uploadStatus === "SUCCESS");

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.user-bulk-upload.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.user-overview.title"),
                to: routes.users.list({
                  slug,
                }),
              },
              {
                name: t("page.user-bulk-upload.title"),
              },
            ]}
          />
        }
        actions={
          <>
            {data && isBulkUploadSuccessful && (
              <Button
                onClick={() => {
                  setData(undefined);
                }}
              >
                {t("page.user-bulk-upload.reset")}
              </Button>
            )}
            {!data && (
              <UploadFileButton
                disabled={isUploading}
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleUploadFile}
              >
                {t("page.user-bulk-upload.button")}
              </UploadFileButton>
            )}
          </>
        }
      >
        <SectionCard>
          {data && (
            <Stack gap="xl">
              <DataTable
                data={data}
                columns={columns}
                dataState={dataState!}
                onRemoveRow={removeRow}
                onUpdateValue={updateRow}
                disabled={isUploading}
                data-testid="users-data-table"
              />
              {dataState?.valid && (
                <Form.Root formMethods={formMethods} id={USER_BULK_UPLOAD_FORM_ID} onSubmit={handleUpload} noPrompt>
                  {sessionUser.isSuperAdmin && (
                    <Form.Body>
                      <FormField label={t("page.user-bulk-upload.form.sign-off-user")} required>
                        <FormSearchableSelect<FormValues, UserDto>
                          name="signOffUser"
                          disabled={isUploading}
                          items={admins}
                          keySelector={(x) => x.id}
                          renderOption={(x) => <User user={x} shouldNavigate={false} />}
                          rules={{
                            required: t("components.form.error.required", {
                              inputName: t("page.user-bulk-upload.form.sign-off-user"),
                            }),
                          }}
                          searchableFieldSelector={(x) => [x.fullName, x.email || ""]}
                        />
                      </FormField>
                    </Form.Body>
                  )}
                </Form.Root>
              )}

              {!isBulkUploadSuccessful && (
                <Form.Footer
                  renderPrimaryAction={() => (
                    <>
                      {isConfirmationRequired && (
                        <Button onClick={handleClickUpload} isLoading={isUploading} disabled={isSubmitDisabled}>
                          {t("common.action.continue")}
                        </Button>
                      )}
                      {!isConfirmationRequired && (
                        <Button
                          data-testid="submit-btn"
                          type="submit"
                          form={USER_BULK_UPLOAD_FORM_ID}
                          isLoading={isUploading}
                          disabled={isSubmitDisabled}
                        >
                          {t("page.user-bulk-upload.submit")}
                        </Button>
                      )}
                    </>
                  )}
                />
              )}
            </Stack>
          )}
          {!data && isParsingFile && <FullSizeLoader withPadding />}
          {!data && !isParsingFile && (
            <>
              <p className="max-w-prose whitespace-pre-wrap">{t("page.user-bulk-upload.prose")}</p>
              <Button variant="ghostPrimary" icon={<Download01Icon />} onClick={handleDownloadExampleFile}>
                {t("page.user-bulk-upload.example-file")}
              </Button>
            </>
          )}
        </SectionCard>
      </DocumentPage>
      <UserBulkUploadConfirmModal
        isSubmitting={isUploading}
        isOpened={isConfirmModalOpened}
        onOpenChange={confirmModalHandler.set}
      />
    </>
  );
}

function preprocessData<T extends readonly Column<{ name: "Email" }>[]>(data: Data<T>): Data<T> {
  // Note: not type safe
  return data
    .flatMap((row: any) => {
      if ("Email" in row && row.Email?.includes(";")) {
        const emails = row.Email.split(";")
          .map((x: string) => x.trim())
          .filter((x: string) => x.length > 0);

        return emails.map((email: string) => ({ ...row, Email: email }));
      }

      return [row];
    })
    .map((row) => ({
      ...row,
      Email: cleanupEmail(row.Email),
    }));
}

function cleanupEmail(dirtyEmail: unknown) {
  if (typeof dirtyEmail !== "string") {
    return dirtyEmail;
  }

  let email = dirtyEmail.trim();

  if (email.startsWith("mailto:")) {
    email = email.slice(7);
  }

  if (email.endsWith(".") || email.endsWith(",")) {
    email = email.slice(0, -1);
  }

  return email;
}
